import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const ACTION_LABELS = {
  document_uploaded: { label: 'Document Uploaded', icon: '📤', color: '#3B82F6' },
  document_downloaded: { label: 'Document Downloaded', icon: '⬇️', color: '#8B5CF6' },
  document_deleted: { label: 'Document Deleted', icon: '🗑️', color: '#EF4444' },
  document_status_changed: { label: 'Status Changed', icon: '✅', color: '#10B981' },
  payment_succeeded: { label: 'Payment Received', icon: '💰', color: '#10B981' },
  payment_failed: { label: 'Payment Failed', icon: '❌', color: '#EF4444' },
  user_login: { label: 'User Login', icon: '👤', color: '#F59E0B' },
}

export default function AuditLogPage() {
  const { profile } = useAuth()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterAction, setFilterAction] = useState('all')
  const [filterClient, setFilterClient] = useState('all')
  const [clients, setClients] = useState([])
  const [search, setSearch] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    const { data: c } = await supabase
      .from('clients')
      .select('id, full_name, business_name')
      .eq('cpa_id', profile.id)
    setClients(c || [])

    const { data: l } = await supabase
      .from('activity_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)
    setLogs(l || [])
    setLoading(false)
  }

  const filtered = logs.filter(l => {
    const matchAction = filterAction === 'all' || l.action === filterAction
    const matchClient = filterClient === 'all' || l.client_id === filterClient
    const matchSearch = search === '' ||
      l.details?.file_name?.toLowerCase().includes(search.toLowerCase()) ||
      l.details?.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      l.details?.email?.toLowerCase().includes(search.toLowerCase()) ||
      l.action?.toLowerCase().includes(search.toLowerCase())
    return matchAction && matchClient && matchSearch
  })

  function formatTime(ts) {
    const d = new Date(ts)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }

  function getDescription(log) {
    const m = log.details || {}
    switch (log.action) {
      case 'document_uploaded': return `${m.file_name} uploaded for ${m.client_name}`
      case 'document_downloaded': return `${m.file_name} downloaded for ${m.client_name}`
      case 'document_deleted': return `${m.file_name} deleted for ${m.client_name}`
      case 'document_status_changed': return `${m.file_name} marked as ${m.new_status}`
      case 'payment_succeeded': return `Invoice ${m.invoice_number} paid — $${m.amount}`
      case 'payment_failed': return `Payment failed for Invoice ${m.invoice_number}`
      case 'user_login': return `${m.email} logged in`
      default: return log.action
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 24, background: '#202326', border: 'none', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 6 }}>Audit Log</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>
              Track all document activity, logins, and payments across your portal.
            </p>
          </div>
          <div style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#CBB17B', fontWeight: 600 }}>
            {filtered.length} Events
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          className="input" style={{ width: 220 }}
          placeholder="Search logs…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <select className="input" style={{ width: 200 }} value={filterAction} onChange={e => setFilterAction(e.target.value)}>
          <option value="all">All Actions</option>
          {Object.entries(ACTION_LABELS).map(([key, val]) => (
            <option key={key} value={key}>{val.icon} {val.label}</option>
          ))}
        </select>
        <select className="input" style={{ width: 200 }} value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="all">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.full_name}</option>)}
        </select>
        <button className="btn btn-ghost" onClick={loadData} style={{ marginLeft: 'auto' }}>🔄 Refresh</button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Uploads', action: 'document_uploaded', icon: '📤', color: '#3B82F6' },
          { label: 'Downloads', action: 'document_downloaded', icon: '⬇️', color: '#8B5CF6' },
          { label: 'Payments', action: 'payment_succeeded', icon: '💰', color: '#10B981' },
          { label: 'Logins', action: 'user_login', icon: '👤', color: '#F59E0B' },
        ].map(s => (
          <div key={s.action} className="card" style={{ padding: '16px 18px', cursor: 'pointer', border: filterAction === s.action ? `2px solid ${s.color}` : '1px solid var(--border)' }}
            onClick={() => setFilterAction(filterAction === s.action ? 'all' : s.action)}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{logs.filter(l => l.action === s.action).length}</div>
            <div style={{ fontSize: 12, color: '#62615D' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Log entries */}
      {loading ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {[...Array(8)].map((_, i) => <div key={i} className="skeleton" style={{ height: 60 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <h3 style={{ marginBottom: 8 }}>No activity yet</h3>
          <p style={{ color: '#62615D', fontSize: 13 }}>Activity will appear here as your team and clients use ClearFile.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {filtered.map((log, i) => {
            const action = ACTION_LABELS[log.action] || { label: log.action, icon: '📝', color: '#62615D' }
            return (
              <div key={log.id} style={{
                padding: '14px 20px',
                display: 'flex', alignItems: 'center', gap: 14,
                borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                background: i % 2 === 0 ? '#fff' : '#FAFAF8'
              }}>
                {/* Icon */}
                <div style={{
                  width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                  background: `${action.color}15`,
                  border: `1px solid ${action.color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16
                }}>
                  {action.icon}
                </div>

                {/* Description */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#202326' }}>{getDescription(log)}</div>
                  <div style={{ fontSize: 11, color: '#62615D', marginTop: 2 }}>
                    {action.label} · {formatTime(log.created_at)}
                  </div>
                </div>

                {/* Badge */}
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
                  background: `${action.color}15`, color: action.color, flexShrink: 0
                }}>
                  {action.label}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
